import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import WebpageTrackingSettings from '@/views/SettingsView/WebpageSettingsView/WebpageTrackingSettings';
import WebpageEKulturIntegrationsSettings from '@/views/SettingsView/WebpageSettingsView/WebpageEKulturIntegrationsSettings';
import WebpageIconsSettings from '@/views/SettingsView/WebpageSettingsView/WebpageIconsSettings';
import WebpageConsentSettings from '@/views/SettingsView/WebpageSettingsView/WebpageConsentSettings';
import WebpageSystemSettings from '@/views/SettingsView/WebpageSettingsView/WebpageSystemSettings';
import WebpageContactInfoSettings from '@/views/SettingsView/WebpageSettingsView/WebpageContactInfoSettings';
import SaveFormButton from '@/components/SaveFormButton';
import Container from '@/components/Container';
import { Typography } from '@mui/material';
import { ChildrenProp } from '@/declarations/ChildrenProp';
import { Api } from '@/services/Api';
import { useStore } from '@/components/store/Store';
import { Site } from '@/declarations/models/Site';
import { useController } from 'react-hook-form';
import SiteSharingBlockSettings from '@/editor/PageEditor/EditorBlock/SiteSharingBlockSettings';
import SettingsInputContainer from '../../../components/SettingsInputContainer';
import BaseSettingsViewForm from '../../BaseSettingsViewForm';

export const SiteSearchSettings: FC = () => {
  const { t } = useTranslation('components');
  const {
    field: { value: searchable_site_ids, onChange: setSearchIds },
  } = useController<Site, `searchable_site_ids`>({
    name: `searchable_site_ids`,
  });

  const handleSetSearchableSiteIds = (ids: string) => {
    setSearchIds(ids);
  };

  return (
    <SettingsInputContainer>
      <SiteSharingBlockSettings
        siteIds={searchable_site_ids}
        setSiteIds={handleSetSearchableSiteIds}
        selectSitesLabel='Settings.WebpageSettings.WebpageSearchSettings.description'
        hideOwnSite
      />
    </SettingsInputContainer>
  );
};

export const WebpageSettingsView: FC<ChildrenProp> = () => {
  const { t: tComponents } = useTranslation('components');
  const { t: tCommon } = useTranslation('common');
  const { enqueueSnackbar } = useSnackbar();
  const { state, setSiteData } = useStore();
  const selectedSiteId = state.selectedSite?.id || 0;

  const handleSaveSettingsForm = useCallback(
    async (site: Site): Promise<void | Site> => {
      const savedFormData = await Api.saveSiteSettings<Site>(site).fetchDirect(null);
      if (!savedFormData) {
        enqueueSnackbar(tCommon('saveFailed'), { variant: 'error' });
      } else {
        // const updatedSite = await Api.getSite(selectedSiteId).fetchDirect(null);
        setSiteData(savedFormData);

        enqueueSnackbar(tCommon('saved'), { variant: 'success' });
        return savedFormData;
      }
    },
    [enqueueSnackbar, tCommon, setSiteData],
  );

  const handleLoadModel = () => {
    return Api.getSite(selectedSiteId);
  };

  const otherLanguageCodes = state.selectedSite?.site_contents
    ?.filter((content) => content.locale !== state.selectedSite?.default_language)
    .map((content) => content.locale);

  return (
    <BaseSettingsViewForm<Site>
      title=''
      onSubmit={handleSaveSettingsForm}
      loadModel={handleLoadModel}
      withLayout={false}>
      <Container left fullWidth p={2}>
        <Typography sx={{ mt: 2, fontSize: 'xx-large' }} variant='h1'>
          {tComponents(`Settings.WebpageSettings.General`)}
        </Typography>
      </Container>
      <Container left fullWidth column p={2} sx={{ overflow: 'auto' }}>
        <Typography>
          {tComponents(`Settings.WebpageSettings.SiteID`)}: <strong>{selectedSiteId}</strong>
        </Typography>
        <Typography>
          {tComponents(`Settings.WebpageSettings.SiteName`)}: <strong>{state.selectedSite?.name}</strong> /{' '}
          {state.selectedSite?.description || '-'}
        </Typography>
        <Typography>
          {tComponents(`Settings.WebpageSettings.SiteDefaultLanguage`)}:{' '}
          <strong>{tCommon(`Languages.${state.selectedSite?.default_language}`)} </strong>
          {otherLanguageCodes && otherLanguageCodes?.length > 0 && (
            <>
              + ({otherLanguageCodes?.length}):
              {otherLanguageCodes?.map((code) => tCommon(`Languages.${code}`)).join(', ')}
            </>
          )}
        </Typography>
        <Typography>
          {tComponents(`Settings.WebpageSettings.SiteUrl`)}: <strong>{state.selectedSite?.url}</strong>
        </Typography>
        <Typography variant='h2' sx={{ fontSize: 'x-large', mt: 3, mb: 1 }}>
          {tComponents(`Settings.WebpageSettings.WebpageConsentSettings.title`)}
        </Typography>
        <WebpageConsentSettings />
        <Typography variant='h2' sx={{ fontSize: 'x-large', mt: 5, mb: 1 }}>
          {tComponents(`Settings.WebpageSettings.WebpageTrackingSettings.title`)}
        </Typography>
        <WebpageTrackingSettings />
        <Typography variant='h2' sx={{ fontSize: 'x-large', mt: 5, mb: 1 }}>
          {tComponents(`Settings.WebpageSettings.WebpageEKulturIntegrationsSettings.title`)}
        </Typography>
        <WebpageEKulturIntegrationsSettings />
        <Typography variant='h2' sx={{ fontSize: 'x-large', mt: 5, mb: 1 }}>
          {tComponents(`Settings.WebpageSettings.WebpageSearchSettings.title`)}
        </Typography>
        <SiteSearchSettings />
        <Typography variant='h2' sx={{ fontSize: 'x-large', mt: 5, mb: 1 }}>
          {tComponents(`Settings.WebpageSettings.WebpageIconsSettings.title`)}
        </Typography>
        <WebpageIconsSettings />
        <Typography variant='h2' sx={{ fontSize: 'x-large', mt: 5, mb: 1 }}>
          {tComponents(`Settings.WebpageSettings.WebpageContactInfoSettings.title`)}
        </Typography>
        <WebpageContactInfoSettings />
        <Typography variant='h2' sx={{ fontSize: 'x-large', mt: 5, mb: 1 }}>
          {tComponents(`Settings.WebpageSettings.WebpageSystemSettings.title`)}
        </Typography>
        <Typography sx={{ mt: 1, mb: 1 }}>
          {tComponents(`Settings.WebpageSettings.WebpageSystemSettings.description`)}
        </Typography>
        <WebpageSystemSettings />
        <Container sx={{ position: 'fixed', top: '100px', right: '50px', backgroundColor: '#efefef', padding: '1em' }}>
          <SaveFormButton />
        </Container>
      </Container>
    </BaseSettingsViewForm>
  );
};

export default WebpageSettingsView;
